import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, ArrowRight, Hammer, Info } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { calculateBuildCost } from '../utils/buildPricing';
import { formatPrice } from '../utils/pricing';

export const BuildCostEstimate = () => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [showEstimate, setShowEstimate] = useState(false);
  const [formData, setFormData] = useState({
    propertyType: '',
    projectType: '',
    specificType: '',
    squareMeters: '',
    quality: 'mid-range',
    needsPartyWall: false,
    postcode: '',
    name: '',
    email: '',
    phone: ''
  });

  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (formData.propertyType || formData.projectType) {
      const calc = calculateBuildCost(formData);
      setEstimate(calc);
      if (formData.projectType && formData.specificType && formData.quality) {
        setShowEstimate(true);
      }
    }
  }, [formData]);

  const propertyTypes = [
    { value: 'detached', label: 'Detached', icon: '🏠' },
    { value: 'semi-detached', label: 'Semi-Detached', icon: '🏘️' },
    { value: 'terraced', label: 'Terraced', icon: '🏚️' },
    { value: 'flat', label: 'Flat', icon: '🏢' },
    { value: 'bungalow', label: 'Bungalow', icon: '🏡' }
  ];

  const projectTypes = [
    { value: 'extension', label: 'Ground Floor', sublabel: 'Extension' },
    { value: 'loft', label: 'Loft', sublabel: 'Conversion' },
    { value: 'first-floor', label: 'First Floor', sublabel: 'Extension' },
    { value: 'basement', label: 'Basement', sublabel: 'Conversion' },
    { value: 'other', label: 'Other', sublabel: 'Projects' }
  ];

  const extensionTypes = [
    { value: 'rear', label: 'Rear', icon: '⬅️' },
    { value: 'side', label: 'Side', icon: '↔️' },
    { value: 'side-infill', label: 'Side Infill', icon: '◼️' },
    { value: 'wraparound', label: 'Wraparound', icon: '🔄' },
    { value: 'side-and-rear', label: 'Side and Rear', icon: '↙️' },
    { value: 'two-storey', label: 'Two Storey', icon: '⬆️' }
  ];

  const loftTypes = [
    { value: 'dormer', label: 'Main Dormer', icon: '🏠' },
    { value: 'outrigger', label: 'Outrigger Dormer', icon: '📐' },
    { value: 'hip-to-gable-main', label: 'Hip to Gable with Dormer', icon: '🏛️' },
    { value: 'hip-to-gable-no', label: 'Hip to Gable (No Dormer)', icon: '📏' },
    { value: 'l-shaped', label: 'L Shaped Dormer', icon: '📐' }
  ];

  const firstFloorTypes = [
    { value: 'side-and-rear', label: 'Side and Rear', icon: '↙️' },
    { value: 'rear', label: 'Rear', icon: '⬅️' },
    { value: 'rear-over-outrigger', label: 'Rear Over Outrigger', icon: '⬆️' },
    { value: 'rear-over-ground', label: 'Rear Over Ground', icon: '⬆️' },
    { value: 'side', label: 'Side', icon: '↔️' }
  ];

  const basementTypes = [
    { value: 'full', label: 'Full Conversion', icon: '🏗️' },
    { value: 'partial', label: 'Partial Conversion', icon: '🔨' },
    { value: 'extension', label: 'Basement Extension', icon: '⬇️' }
  ];

  const otherTypes = [
    { value: 'garage-conversion', label: 'Garage Conversion', icon: '🚗' },
    { value: 'garden-annex', label: 'Garden Annex', icon: '🌳' },
    { value: 'two-storey-rear', label: 'Two Storey Rear', icon: '⬆️' },
    { value: 'two-storey-side', label: 'Two Storey Side', icon: '⬆️' }
  ];

  const qualityLevels = [
    { value: 'standard', label: 'Standard', desc: 'Good quality materials', price: '£2,200/m²' },
    { value: 'mid-range', label: 'Mid-Range', desc: 'Premium finishes', price: '£2,750/m²', recommended: true },
    { value: 'high-end', label: 'High-End', desc: 'Luxury materials', price: '£3,520/m²' },
    { value: 'luxury', label: 'Luxury', desc: 'Bespoke everything', price: '£4,400/m²' }
  ];

  const handleSelect = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    if (field === 'propertyType') {
      setTimeout(() => setCurrentStep(2), 300);
    } else if (field === 'projectType') {
      setTimeout(() => setCurrentStep(3), 300);
    } else if (field === 'specificType') {
      setTimeout(() => setCurrentStep(4), 300);
    } else if (field === 'quality') {
      setTimeout(() => setCurrentStep(5), 300);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Submit the build cost estimate request to the backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/architecture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          project_type: `Build Cost Estimate - ${formData.projectType}${formData.specificType ? ' - ' + formData.specificType : ''}`,
          property_address: `${formData.propertyType} property`,
          postcode: formData.postcode,
          budget_range: estimate ? `Build: ${formatPrice(estimate.totalMin)} - ${formatPrice(estimate.totalMax)}` : 'Estimate requested',
          project_description: `Property Type: ${formData.propertyType}, Project: ${formData.projectType}, Specific: ${formData.specificType || 'N/A'}, Size: ${formData.squareMeters}m², Quality: ${formData.quality}, Party Wall: ${formData.needsPartyWall ? 'Yes' : 'No'}`,
          preferred_contact: 'email'
        })
      });
      
      if (response.ok) {
        toast({
          title: "Build Cost Estimate Sent!",
          description: "We'll email your detailed breakdown within 1 hour.",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to submit estimate request. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Build estimate submission error:', error);
      toast({
        title: "Error",
        description: "Failed to submit estimate request. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getSpecificTypes = () => {
    switch (formData.projectType) {
      case 'extension': return extensionTypes;
      case 'loft': return loftTypes;
      case 'first-floor': return firstFloorTypes;
      case 'basement': return basementTypes;
      case 'other': return otherTypes;
      default: return [];
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Price Display */}
      {showEstimate && estimate && (
        <div className="fixed top-20 right-0 left-0 z-40 bg-gradient-to-r from-[#404040] to-[#2a2a2a] text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Hammer className="w-6 h-6 text-[#FF8C42]" />
                <div>
                  <div className="text-sm opacity-90">Your Estimated Build Cost</div>
                  <div className="text-2xl font-bold">
                    {formatPrice(estimate.totalMin)} - {formatPrice(estimate.totalMax)}
                  </div>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-6">
                <div>
                  <div className="text-xs opacity-90">{estimate.squareMeters}m² @ {formatPrice(estimate.costPerSqM)}/m²</div>
                  <div className="font-semibold">{formatPrice(estimate.baseBuildCost)}</div>
                </div>
                <div className="border-l border-white/30 pl-6">
                  <div className="text-xs opacity-90">Including All Fees</div>
                  <div className="text-sm">Structural • Building Control • Contingency</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${showEstimate ? 'pt-32' : 'pt-24'} pb-16`}>
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <div className="inline-flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-full mb-4">
            <Hammer className="w-5 h-5 text-[#404040]" />
            <span className="text-[#404040] font-semibold">Build Cost Calculator</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-[#404040] mb-4">
            Instant Build Cost Estimate
          </h1>
          <p className="text-lg text-gray-600">
            Get an accurate construction cost estimate for your project
          </p>
        </div>

        {/* Form Card */}
        <Card className="border-none shadow-2xl overflow-hidden">
          <CardContent className="p-0">
            <form onSubmit={handleSubmit}>
              {/* Steps 1-3: Same as design quote */}
              {currentStep === 1 && (
                <div className="p-8 md:p-12 fade-in">
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    What type of property?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {propertyTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleSelect('propertyType', type.value)}
                        className={`p-6 rounded-xl border-2 transition-all hover:scale-105 ${
                          formData.propertyType === type.value
                            ? 'border-[#404040] bg-gray-50 shadow-md'
                            : 'border-gray-200 hover:border-[#404040]'
                        }`}
                      >
                        <div className="text-5xl mb-3">{type.icon}</div>
                        <div className="font-semibold text-[#404040] text-sm">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="p-8 md:p-12 fade-in">
                  <button type="button" onClick={() => setCurrentStep(1)} className="text-[#404040] hover:underline mb-6 text-sm">
                    ← Back
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    What are you building?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {projectTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleSelect('projectType', type.value)}
                        className={`p-6 rounded-xl border-2 transition-all hover:scale-105 ${
                          formData.projectType === type.value
                            ? 'border-[#404040] bg-gray-50 shadow-md'
                            : 'border-gray-200 hover:border-[#404040]'
                        }`}
                      >
                        <div className="font-bold text-[#404040] mb-1">{type.label}</div>
                        <div className="text-sm text-gray-600">{type.sublabel}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="p-8 md:p-12 fade-in">
                  <button type="button" onClick={() => setCurrentStep(2)} className="text-[#404040] hover:underline mb-6 text-sm">
                    ← Back
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    Specific type of <span className="text-[#FF8C42]">{formData.projectType}</span>?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {getSpecificTypes().map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleSelect('specificType', type.value)}
                        className={`p-6 rounded-xl border-2 transition-all hover:scale-105 ${
                          formData.specificType === type.value
                            ? 'border-[#404040] bg-gray-50 shadow-md'
                            : 'border-gray-200 hover:border-[#404040]'
                        }`}
                      >
                        <div className="text-4xl mb-3">{type.icon}</div>
                        <div className="font-semibold text-[#404040] text-sm">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Quality & Size */}
              {currentStep === 4 && (
                <div className="p-8 md:p-12 fade-in">
                  <button type="button" onClick={() => setCurrentStep(3)} className="text-[#404040] hover:underline mb-6 text-sm">
                    ← Back
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    Quality level & size
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {qualityLevels.map((level) => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => handleSelect('quality', level.value)}
                        className={`p-6 rounded-xl border-2 transition-all text-left relative ${
                          formData.quality === level.value
                            ? 'border-[#404040] bg-gray-50 shadow-md'
                            : 'border-gray-200 hover:border-[#404040]'
                        }`}
                      >
                        {level.recommended && (
                          <span className="absolute top-2 right-2 bg-[#FF8C42] text-white text-xs px-2 py-1 rounded-full">
                            Recommended
                          </span>
                        )}
                        <div className="font-bold text-[#404040] mb-1">{level.label}</div>
                        <div className="text-sm text-gray-600 mb-2">{level.desc}</div>
                        <div className="text-[#FF8C42] font-semibold">{level.price}</div>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="sqm" className="text-[#404040] font-medium mb-2 block flex items-center">
                        Square Meters (Optional)
                        <div className="group relative ml-2">
                          <Info className="w-4 h-4 text-gray-400" />
                          <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded">
                            Leave blank for our estimate based on your project type
                          </div>
                        </div>
                      </Label>
                      <Input
                        id="sqm"
                        type="number"
                        placeholder="e.g., 25"
                        value={formData.squareMeters}
                        onChange={(e) => setFormData({ ...formData, squareMeters: e.target.value })}
                        className="text-lg"
                      />
                      {estimate && !formData.squareMeters && (
                        <p className="text-sm text-gray-500 mt-2">
                          Estimated: {estimate.squareMeters}m² based on typical {formData.specificType} projects
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="partyWall"
                        checked={formData.needsPartyWall}
                        onChange={(e) => setFormData({ ...formData, needsPartyWall: e.target.checked })}
                        className="w-5 h-5 text-[#FF8C42] border-gray-300 rounded"
                      />
                      <Label htmlFor="partyWall" className="text-[#404040] font-medium">
                        Requires Party Wall Agreement (+£1,800)
                      </Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Contact Details */}
              {currentStep === 5 && (
                <div className="p-8 md:p-12 fade-in">
                  <button type="button" onClick={() => setCurrentStep(4)} className="text-[#404040] hover:underline mb-6 text-sm">
                    ← Back
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-2">
                    Get Your Detailed Breakdown
                  </h2>
                  <p className="text-gray-600 mb-8">We'll email your comprehensive build cost analysis</p>
                  
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="postcode" className="text-[#404040] font-medium mb-2 block">
                        Property Postcode *
                      </Label>
                      <Input
                        id="postcode"
                        placeholder="e.g., SW1A 1AA"
                        value={formData.postcode}
                        onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                        required
                        className="text-lg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="name" className="text-[#404040] font-medium mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="text-lg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#404040] font-medium mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="text-lg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#404040] font-medium mb-2 block">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="020 1234 5678"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="text-lg"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-[#404040] hover:bg-[#2a2a2a] text-white text-lg py-6 btn-hover-lift"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Send Me Build Cost Breakdown
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
